import { Colors } from '@factorialco/deprecated-design-system'
import { ShiftManagementUnassignedShiftFragment } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { dateToString, stringToDate, timeToString } from 'modules/core/lib/dates'

import locations from 'modules/locations/store/collections/locations'
import workAreas from 'modules/locations/store/collections/workAreas'
import type Location from 'modules/locations/store/models/Location'
import type WorkArea from 'modules/locations/store/models/WorkArea'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import ApiUnassignedShift from 'modules/shift_management/store/api/models/UnassignedShift'
import type Shift from 'modules/shift_management/store/internal/models/Shift'

export type TUnassignedShift = ShiftManagementUnassignedShiftFragment

export interface Props {
  id: number
  startAt: moment.Moment
  endAt: moment.Moment
  locationId: number | null | undefined
  workAreaId: number | null | undefined
  notes: string | null | undefined
  extraHours: boolean
  color: Colors | null
  counter: number
  overnightShift: boolean
  defaultShiftId: number | null | undefined
  defaultShiftTitle: string | null | undefined
  allCompany: boolean
  teamsIdsVisibilities: number[] | null
  locationsIdsVisibilities: number[] | null
  jobCatalogRolesIds: number[]
  jobCatalogTreeRoleUuids: string[]
}

export interface UnassignedShiftPayload {
  id: number
  start_at: string
  end_at: string
  location_id: number | null
  locations_work_area_id: number | null
  notes: string | null
  extra_hours: boolean
  color: string | null
  counter: number
  overnight_shift: boolean
  default_shift_id: number | null
  default_shift_title: string | null
  all_company: boolean
  visible_teams_ids: number[] | null
  visible_locations_ids: number[] | null
  job_catalog_roles_ids: number[]
  job_catalog_tree_role_uuids: string[]
}

class UnassignedShift {
  id: number
  startAt: moment.Moment
  endAt: moment.Moment
  locationId: number | null
  workAreaId: number | null
  notes: string | null
  extraHours: boolean
  color: Colors | null
  counter: number
  overnightShift: boolean
  defaultShiftId: number | null
  defaultShiftTitle: string | null
  allCompany: boolean
  teamsIdsVisibilities: number[] | null
  locationsIdsVisibilities: number[] | null
  jobCatalogRolesIds: number[]
  jobCatalogTreeRoleUuids: string[]

  constructor(props: Props) {
    this.id = props.id
    this.startAt = props.startAt.clone()
    this.endAt = props.endAt.clone()
    this.locationId = props.locationId || null
    this.workAreaId = props.workAreaId || null
    this.notes = props.notes || null
    this.extraHours = !!props.extraHours
    this.color = props.color || null
    this.counter = props.counter
    this.overnightShift = props.overnightShift
    this.defaultShiftId = props.defaultShiftId || null
    this.defaultShiftTitle = props.defaultShiftTitle || null
    this.allCompany = !!props.allCompany
    this.teamsIdsVisibilities = props.teamsIdsVisibilities || null
    this.locationsIdsVisibilities = props.locationsIdsVisibilities || null
    this.jobCatalogRolesIds = props.jobCatalogRolesIds || []
    this.jobCatalogTreeRoleUuids = props.jobCatalogTreeRoleUuids || []

    makeObservable(this, {
      startTime: computed,
      endTime: computed,
      location: computed,
      workArea: computed,
      isDefault: computed,
      isOvertime: computed,
      startAtDate: computed,
      endAtDate: computed,
    })
  }

  /**
   * @deprecated Before using this method, consider using the `buildFromGraphQL` hook since
   * we are trying to move away from using mobx-rest and use GraphQL instead.
   */
  static buildFromProps(props: UnassignedShiftPayload): UnassignedShift {
    const color = props.color ? nameFromHexCode(props.color) : null

    return new UnassignedShift({
      id: props.id,
      startAt: this.getDate(props.start_at),
      endAt: this.getDate(props.end_at),
      locationId: props.location_id,
      workAreaId: props.locations_work_area_id,
      notes: props.notes,
      extraHours: props.extra_hours,
      color,
      counter: props.counter,
      overnightShift: props.overnight_shift,
      defaultShiftId: props.default_shift_id,
      defaultShiftTitle: props.default_shift_title,
      allCompany: props.all_company,
      teamsIdsVisibilities: props.visible_teams_ids,
      locationsIdsVisibilities: props.visible_locations_ids,
      jobCatalogRolesIds: props.job_catalog_roles_ids,
      jobCatalogTreeRoleUuids: props.job_catalog_tree_role_uuids || [],
    })
  }

  static buildFromGraphQL(fragment: TUnassignedShift): UnassignedShift {
    const color = fragment.color ? nameFromHexCode(fragment.color) : null

    return new UnassignedShift({
      id: fragment.id,
      startAt: this.getDate(fragment.startAt),
      endAt: this.getDate(fragment.endAt),
      locationId: fragment.locationId,
      workAreaId: fragment.locationsWorkAreaId,
      notes: fragment.notes,
      extraHours: !!fragment.extraHours,
      color,
      counter: fragment.counter,
      overnightShift: fragment.overnightShift,
      defaultShiftId: fragment.defaultShiftId,
      defaultShiftTitle: fragment.defaultShiftTitle,
      allCompany: fragment.allCompany,
      teamsIdsVisibilities: fragment.visibleTeamsIds,
      locationsIdsVisibilities: fragment.visibleLocationsIds,
      jobCatalogRolesIds: fragment.jobCatalogRolesIds,
      jobCatalogTreeRoleUuids: fragment.jobCatalogTreeRoleUuids || [],
    })
  }

  toPayload(): ApiUnassignedShift {
    return new ApiUnassignedShift({
      id: this.id,
      start_at: this.startTimeDate,
      end_at: this.endTimeDate,
      location_id: this.locationId,
      work_area_id: this.workAreaId,
      notes: this.notes,
      extra_hours: this.extraHours,
      color: this.color,
      counter: this.counter,
      overnight_shift: this.overnightShift,
      default_shift_id: this.defaultShiftId,
      default_shift_title: this.defaultShiftTitle,
      all_company: this.allCompany,
      teams_ids_visibilities: this.teamsIdsVisibilities,
      locations_ids_visibilities: this.locationsIdsVisibilities,
      job_catalog_roles_ids: this.jobCatalogRolesIds,
      job_catalog_tree_role_uuids: this.jobCatalogTreeRoleUuids,
    })
  }

  get location(): Location | undefined {
    return locations.find({
      id: this.locationId,
    })
  }

  get workArea(): WorkArea | undefined {
    return workAreas.find({
      id: this.workAreaId,
    })
  }

  static getDate(date: string): moment.Moment {
    return stringToDate(moment(date, 'YYYY-MM-DD HH:mm').format(), 'backend_datetime')
  }

  get startTime(): string {
    return timeToString(this.startAt)
  }

  get endTime(): string {
    return timeToString(this.endAt)
  }

  get startTimeDate(): string {
    return dateToString(this.startAt, 'backend_datetime')
  }

  get endTimeDate(): string {
    return dateToString(this.endAt, 'backend_datetime')
  }

  get startAtDate(): string {
    return dateToString(this.startAt, 'backend')
  }

  get endAtDate(): string {
    return dateToString(this.endAt, 'backend')
  }

  get isDefault(): boolean {
    return !!this.defaultShiftId
  }

  get isOvertime(): boolean {
    return !!this.extraHours
  }

  isDate(date: moment.Moment): boolean {
    return dateToString(this.startAt, 'backend') === dateToString(date, 'backend')
  }

  inRange(firstDay: moment.Moment, lastDay: moment.Moment): boolean {
    const format = 'YYYY-MM-DD HH:mm'
    const formattedCurrentStartDate = this.startAt.format(format)
    const formattedFirstDay = firstDay.format(format)
    const formattedLastDay = lastDay.format(format)

    return (
      formattedFirstDay <= formattedCurrentStartDate && formattedCurrentStartDate < formattedLastDay
    )
  }

  assignedShifts(shifts: Shift[]): Shift[] {
    return shifts.reduce((result: Shift[], shift: Shift) => {
      if (shift.unassignedShiftId === this.id && !shift.isBackup) result.push(shift)
      return result
    }, [])
  }

  currentCounter(shifts: Shift[]): number {
    return this.counter - this.assignedShifts(shifts).length
  }
}

export default UnassignedShift
